import { useEffect, useRef, useState } from "react";
import { smoothScrollTo } from "../utils/smoothScroll";

const Header = () => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const labelRef = useRef<HTMLLabelElement | null>(null);

  const [isOpen, setIsOpen] = useState(() => false);
  const [btnsVisible, setBtnsVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setBtnsVisible(false);
      } else {
        setBtnsVisible(true);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && labelRef.current && !labelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCheckboxClick = () => {
    setIsOpen(prev => !prev);
  };

  return (
    <>
      <nav
        className={`fixed w-full z-20 top-0 left-0 transition-all duration-300 ${
          scrolled
            ? 'bg-white/90 backdrop-blur-xl border-b border-[#1f2fd6]/10 shadow-sm'
            : 'bg-transparent'
        }`}
        style={{ 'zIndex': 1500 }}
      >
        <div className="max-w-7xl flex flex-wrap items-center justify-between mx-auto px-6 py-4">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2">
            <svg className="h-8 w-8 text-[#0c1aa6]" viewBox="0 0 174 175" fill="currentColor">
              <path d="M86.36,0l.33,73.88,52.33-51.84,14.09,14.09-52,52.01.33.65h67.24v19.44h-68.05l52.14,52.06-14.39,14.38-44.89-44.23c-10.73-11.35,4.46-43.07-14.64-48.22-12.79-3.44-29.48,3.88-39.63-6.71L0,36.29l14.22-14.23,52.38,51.82V0h19.77Z"/>
              <path d="M68.91,144.75c-.71-13.96-1.42-27.92-2.14-41.88-13.21-.38-26.41-.76-39.62-1.15,2.78-3.39,5.57-6.78,8.35-10.17,10.79.17,21.57.33,32.36.5.78.08,4.51.57,7.22,3.86,2.21,2.69,2.41,5.65,2.43,6.59.31,10.73.62,21.45.93,32.18-3.18,3.36-6.35,6.72-9.53,10.08Z"/>
            </svg>
            <span className="text-[#212223] font-semibold text-xl tracking-tight">Arkspace</span>
          </a>

          {/* Desktop Navigation */}
          <div className="flex items-center md:order-2 gap-2">
            {btnsVisible ? (
              <>
                <div className="flex items-center gap-1 mr-4">
                  <button
                    onClick={() => { smoothScrollTo('labs'); setIsOpen(false) }}
                    className="px-4 py-2 text-sm text-[#212223] hover:text-[#1f2fd6] transition-colors"
                  >
                    Labs
                  </button>
                  <button
                    onClick={() => { smoothScrollTo('go'); setIsOpen(false) }}
                    className="px-4 py-2 text-sm text-[#212223] hover:text-[#1f2fd6] transition-colors"
                  >
                    Go
                  </button>
                  <button
                    onClick={() => { smoothScrollTo('about'); setIsOpen(false) }}
                    className="px-4 py-2 text-sm text-[#212223] hover:text-[#1f2fd6] transition-colors"
                  >
                    Sobre
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => smoothScrollTo('contact')}
                  className="text-white bg-[#1f2fd6] hover:bg-[#0c1aa6] font-medium rounded-lg text-sm px-5 py-2.5 transition-all hover:shadow-lg hover:shadow-[#1f2fd6]/25"
                >
                  Fale conosco
                </button>
              </>
            ) : null}

            {/* Mobile Menu Button */}
            {!btnsVisible ? (
              <label
                ref={labelRef}
                className="btn border border-none pr-0 swap swap-rotate bg-transparent"
              >
                <input
                  className="invisible menu-checkbox"
                  type="checkbox"
                  checked={isOpen}
                  onChange={handleCheckboxClick}
                />
                <svg
                  className={`swap-off fill-[#212223]`}
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  viewBox="0 0 512 512"
                >
                  <path d="M64,384H448V341.33H64Zm0-106.67H448V234.67H64ZM64,128v42.67H448V128Z" />
                </svg>
                <svg
                  className={`swap-on fill-[#212223]`}
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 512 512"
                >
                  <polygon points="400 145.49 366.51 112 256 222.51 145.49 112 112 145.49 222.51 256 112 366.51 145.49 400 256 289.49 366.51 400 400 366.51 289.49 256 400 145.49" />
                </svg>
              </label>
            ) : null}
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-[#212223]/50 backdrop-blur-sm"
          style={{ 'zIndex': '1499' }}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <div
        ref={dropdownRef}
        className={`fixed top-0 right-0 h-full w-[280px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } lg:hidden`}
        style={{ "zIndex": "1500" }}
      >
        <div className="flex flex-col p-6 pt-20">
          <button
            onClick={() => { smoothScrollTo('labs'); handleCheckboxClick() }}
            className="py-4 text-left text-lg text-[#212223] hover:text-[#1f2fd6] border-b border-[#1f2fd6]/10 transition-colors"
          >
            Labs
          </button>
          <button
            onClick={() => { smoothScrollTo('go'); handleCheckboxClick() }}
            className="py-4 text-left text-lg text-[#212223] hover:text-[#1f2fd6] border-b border-[#1f2fd6]/10 transition-colors"
          >
            Go
          </button>
          <button
            onClick={() => { smoothScrollTo('about'); handleCheckboxClick() }}
            className="py-4 text-left text-lg text-[#212223] hover:text-[#1f2fd6] border-b border-[#1f2fd6]/10 transition-colors"
          >
            Sobre
          </button>
          <button
            onClick={() => { smoothScrollTo('contact'); handleCheckboxClick() }}
            className="mt-6 w-full text-white bg-[#1f2fd6] hover:bg-[#0c1aa6] font-medium rounded-lg text-sm px-5 py-3 transition-all"
          >
            Fale conosco
          </button>
        </div>
      </div>
    </>
  )
}

export default Header;
